import { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useSharedRun } from '../hooks/useRunShares'
import { ExportPreview } from '../components/run-builder/ExportPreview'
import { SharedSegmentsView } from '../components/shared/SharedSegmentsView'
import { SharedAidStationsView } from '../components/shared/SharedAidStationsView'
import { SharedNutritionView } from '../components/shared/SharedNutritionView'
import { CrewAidStationsView } from '../components/crew/CrewAidStationsView'
import { CrewSummary } from '../components/crew/CrewSummary'
import { CrewModeToggle, CrewModeButton } from '../components/crew/CrewModeToggle'
import { Button } from '../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Calendar, Clock, FileText, Printer, Share, Utensils } from 'lucide-react'
import { ViewMode } from '../types'

export default function SharedRun() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const { data: sharedData, isLoading, error } = useSharedRun(slug!)
  const [showPrintView, setShowPrintView] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('full')
  const [showElapsedTime, setShowElapsedTime] = useState(false)

  // Check URL parameters for crew view
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const view = params.get('view')
    if (view === 'crew') {
      setViewMode('crew')
    }
  }, [location.search])

  // Update URL when view mode changes
  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode)
    const newUrl = new URL(window.location.href)
    if (newMode === 'crew') {
      newUrl.searchParams.set('view', 'crew')
    } else {
      newUrl.searchParams.delete('view')
    }
    window.history.replaceState({}, '', newUrl.toString())
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-primary-700 rounded-xl flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">Loading shared run</h2>
                <p className="text-neutral-600">Preparing the race plan for viewing...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6 max-w-md">
              <div className="w-16 h-16 mx-auto bg-accent-600 rounded-xl flex items-center justify-center">
                <Share className="w-8 h-8 text-white" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">Run Not Found</h2>
                <p className="text-neutral-600">This run plan is not publicly available or the link has expired.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!sharedData) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-neutral-400 rounded-xl flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-primary-900">No Data Available</h2>
                <p className="text-neutral-600">Unable to load the shared run data.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const run = sharedData.run
  const startTime = run.start_time ? new Date(run.start_time) : undefined

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `${run.title} - UltraPlan`,
        text: `Check out this ultra running plan: ${run.title}`,
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      // Could add a toast notification here
    }
  }

  const handlePrint = () => {
    setShowPrintView(true)
    setTimeout(() => {
      window.print()
      setShowPrintView(false)
    }, 100)
  }

  // If we're in crew mode and there's no aid stations, redirect to full view
  if (viewMode === 'crew' && (!run.aid_stations || run.aid_stations.length === 0)) {
    setViewMode('full')
  }

  if (showPrintView) {
    return (
      <div className="print:block">
        <ExportPreview run={run} format="full" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-6 py-12 max-w-7xl space-y-10">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6">
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-4xl font-bold text-primary-900 mb-3">
                  {run.title}
                </h1>
                <div className="flex flex-wrap items-center gap-6 text-neutral-600">
                  {run.date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary-600" />
                      <span className="font-medium text-lg">{new Date(run.date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary-600" />
                    <span className="font-medium">{(run.segments?.length || 0) + (run.aid_stations?.length || 0)} checkpoints</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Utensils className="h-5 w-5 text-primary-600" />
                    <span className="font-medium">{run.plan_items?.length || 0} nutrition items</span>
                  </div>
                </div>
              </div>

              {run.notes && (
                <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                  <p className="text-neutral-700 leading-relaxed italic">"{run.notes}"</p>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="text-sm text-neutral-500 flex items-center gap-2">
                <Share className="h-4 w-4" />
                <span>Shared via UltraPlan</span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* View Mode Toggle */}
                {run.aid_stations && run.aid_stations.length > 0 && (
                  <div className="hidden sm:block">
                    <CrewModeToggle
                      currentMode={viewMode}
                      onModeChange={handleViewModeChange}
                    />
                  </div>
                )}
                {run.aid_stations && run.aid_stations.length > 0 && (
                  <div className="sm:hidden">
                    <CrewModeButton
                      currentMode={viewMode}
                      onModeChange={handleViewModeChange}
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  {startTime && viewMode === 'crew' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowElapsedTime(!showElapsedTime)}
                      className="border-neutral-300 hover:bg-neutral-50"
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      {showElapsedTime ? 'Clock' : 'T+'}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShare}
                    className="border-neutral-300 hover:bg-neutral-50"
                  >
                    <Share className="h-4 w-4 mr-1" />
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrint}
                    className="border-neutral-300 hover:bg-neutral-50"
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Print
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === 'crew' ? (
          // Crew Mode Content
          <div className="space-y-6">
            <CrewSummary
              aidStations={run.aid_stations || []}
              segments={run.segments || []}
              calibrationProfile={run.calibration_profile}
              slug={slug!}
              runTitle={run.title}
              startTime={startTime}
            />
            
            <CrewAidStationsView
              aidStations={run.aid_stations || []}
              segments={run.segments || []}
              calibrationProfile={run.calibration_profile}
              slug={slug!}
              startTime={startTime}
              showElapsed={showElapsedTime}
              run={run}
            />
          </div>
        ) : (
          // Full Plan Mode Content
          <Tabs defaultValue="overview" className="space-y-8">
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-2">
              <TabsList className="grid w-full grid-cols-3 bg-neutral-50 gap-1 p-1">
                <TabsTrigger
                  value="overview"
                  className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-600 data-[state=active]:shadow-sm rounded-xl transition-all duration-200 font-medium"
                >
                  <FileText className="h-4 w-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="aid-stations"
                  className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-600 data-[state=active]:shadow-sm rounded-xl transition-all duration-200 font-medium"
                >
                  <Clock className="h-4 w-4" />
                  Aid Stations
                </TabsTrigger>
                <TabsTrigger
                  value="nutrition"
                  className="flex items-center gap-2 px-6 py-3 text-neutral-600 bg-transparent hover:bg-white data-[state=active]:bg-white data-[state=active]:text-primary-600 data-[state=active]:shadow-sm rounded-xl transition-all duration-200 font-medium"
                >
                  <Utensils className="h-4 w-4" />
                  Nutrition
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-8">
              <SharedSegmentsView
                segments={run.segments || []}
                aidStations={run.aid_stations || []}
                calibrationProfile={run.calibration_profile}
              />
              <SharedNutritionView
                planItems={run.plan_items || []}
                segments={run.segments || []}
                calibrationProfile={run.calibration_profile}
              />
            </TabsContent>

            <TabsContent value="aid-stations">
              <SharedAidStationsView
                aidStations={run.aid_stations || []}
                segments={run.segments || []}
                calibrationProfile={run.calibration_profile}
              />
            </TabsContent>

            <TabsContent value="nutrition">
              <SharedNutritionView
                planItems={run.plan_items || []}
                segments={run.segments || []}
                calibrationProfile={run.calibration_profile}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  )
}